/* eslint-disable no-console */
import React from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useBedrockRegistration } from 'store/bedrockRegistration';
import { useVertical } from 'lib/Hooks';
import { Button } from 'components/Button';
import { TextField } from 'components/FormFields/TextField';
import { ErrorMsg } from 'components/FormFields/ErrorMsg';
import { SCREENS } from 'lib/loginFormConstants';
import { publisherNameMap } from 'lib/vertical';

import { oneTimeCodeSchema } from '../validationSchema';

import styles from './styles.module.scss';
import globalStyles from '../styles.module.scss';

/**
 * VerifyOTCScreen component for handling log in with a one time code.
 * @param {object} props - The component props.
 * @param {string} [props.userEmail] - The email address associated with the user.
 * @returns {React.ReactElement} The rendered component
 */
const VerifyOTCScreen = ({ userEmail = null }) => {
  const { vertical } = useVertical();
  const brand = publisherNameMap[vertical];
  const emailFromState = useBedrockRegistration((state) => state?.user?.email) || '';
  const requestOneTimeCode = useBedrockRegistration((state) => state.requestOneTimeCode);
  const verifyOneTimeCode = useBedrockRegistration((state) => state.verifyOneTimeCode);
  const screen = useBedrockRegistration((state) => state.screen);
  const setScreen = useBedrockRegistration((state) => state.setScreen);
  const error = useBedrockRegistration((state) => state.error);
  const OtcTitle = screen === SCREENS.VERIFY_OTC ? (
    <>
      Verify your email
      {' '}
      <br />
      address to continue
    </>
  ) : (
    <>Log in with code</>
  );

  const buttonText = screen === SCREENS.VERIFY_OTC ? 'Continue' : 'Log in';
  const emailToRender = userEmail ?? emailFromState;

  const methods = useForm({
    resolver: yupResolver(oneTimeCodeSchema),
  });
  /**
   * Handles form submission
   * @param {object} values - The component props.
   * @param {object} values.oneTimeCode - The one time code sent to the user.
   */
  const onSubmit = ({ oneTimeCode }) => {
    const trackingPayload = {
      action: 'SignIn:Submit',
      method: 'SubmitButton:Click',
    };
    verifyOneTimeCode({ oneTimeCode, brand, trackingPayload }).then((result) => {
      // update the screen value based on the response
      if (result) {
        setScreen(result);
      }
    });
  };

  /**
   * Handles resend code button click
   */
  const resendCode = () => {
    const trackingPayload = {
      action: 'SignIn:Submit',
      method: 'OneTimeCodeButton:Click',
    };
    requestOneTimeCode({ trackingPayload }).then((result) => {
      if (result) setScreen(result);
    });
  };

  return (
    <>
      <h2 data-testid="account-verify-email">{OtcTitle}</h2>
      <ErrorMsg message={error} />
      <p className={globalStyles.description}>
        Enter the one-time code we sent to
      </p>
      <div className={styles.userEmail} data-testid="verifyOTC-userEmail">
        {emailToRender || 'example@example.com'}
      </div>
      <p className={styles.smallTxt}>
        If you couldn&apos;t find our email, please
        <br />
        check your spam folder.
      </p>
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <FormProvider {...methods}>
        <form
          onSubmit={methods.handleSubmit(onSubmit)}
          data-testid="verify-otc"
        >
          <TextField
            dataTestId="otc-input-field"
            name="oneTimeCode"
            label="What is your code?"
            type="one-time-code"
          />
          <Button
            tiltClasses={globalStyles.button}
            isFullWidth
            displayType="submit"
            title={buttonText}
          />
        </form>
      </FormProvider>
      <button
        type="button"
        className={globalStyles.buttonLink}
        onClick={resendCode}
        data-testid="resend-code"
      >
        Send code again
      </button>
    </>
  );
};

export { VerifyOTCScreen };
